(() => {
  'use strict';

  if (customElements.get('fitorb-us-product')) return;

  const englishColour = value => ({grau: 'Grey', schwarz: 'Black', silber: 'Silver', roségold: 'Rose gold', rosegold: 'Rose gold'}[String(value ?? '').toLowerCase()] || value);

  class FitorbPdfProduct extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;

      const source = this.querySelector('[data-fpdf-data]');
      if (!source) return;

      try {
        this.data = JSON.parse(source.textContent);
      } catch (_) {
        return;
      }

      this._connected = true;
      this.abort = new AbortController();
      this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
      this.options = Array.isArray(this.data.options) ? this.data.options : [];
      this.sizeIndex = Number.isInteger(this.data.sizeIndex) ? this.data.sizeIndex : -1;
      this.modelIndex = Number.isInteger(this.data.modelIndex) ? this.data.modelIndex : -1;
      this.colorIndex = Number.isInteger(this.data.colorIndex) ? this.data.colorIndex : -1;
      this.form = this.querySelector('[data-product-form]');
      this.idInput = this.querySelector('[data-variant-id]');
      this.strings = this.data.strings || {};
      this.settings = this.data.settings || {};
      this.busy = false;
      this.galleryIndex = 0;
      this.variantGalleryActive = false;
      this.lastGallerySource = null;
      this.lastVariantId = null;

      const kitIds = new Set((this.data.kitVariantIds || []).map(String));
      this.kitValues = this.sizeIndex < 0
        ? []
        : this.values(this.sizeIndex).filter(value =>
            /probier|sizing|proef|passet|pasring|maatset|kit|ringenset|testset/i.test(value) || value === this.data.kitValue ||
            this.variants.some(variant => kitIds.has(String(variant.id)) && String(variant.options[this.sizeIndex]) === value)
          );

      this.setSelectionFromVariant(this.data.selectedVariant);

      window.addEventListener('popstate', () => {
        this.clearError();
        this.setSelectionFromVariant(
          new URL(location.href).searchParams.get('variant')
        );
        this.render();
      }, { signal: this.abort.signal });

      this.addEventListener('click', this.onClick.bind(this), {
        signal: this.abort.signal,
        capture: true
      });

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this), {
          signal: this.abort.signal,
          capture: true
        });
      }

      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('close', () => {
          dialog.querySelectorAll('video').forEach(video => video.pause());
        }, { signal: this.abort.signal });

        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;

          const box = dialog.getBoundingClientRect();

          if (
            event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom
          ) {
            dialog.close();
          }
        }, { signal: this.abort.signal });
      });

      this.setupGallery();
      this.setupSticky();
      this.render();

      this.shippingTimer = window.setInterval(
        () => this.renderShipping(this.displayVariant()),
        60000
      );

      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.stickyObserver?.disconnect();
      this.resizeObserver?.disconnect();
      clearInterval(this.shippingTimer);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.galleryFrame);
      cancelAnimationFrame(this.variantScrollFrame);
      this._connected = false;
    }

    setSelectionFromVariant(variantId) {
      const explicit = variantId == null
        ? null
        : this.variants.find(variant =>
            String(variant.id) === String(variantId)
          );

      const first =
        explicit ||
        this.variants.find(variant => variant.available) ||
        this.variants[0];

      this.selected = first
        ? first.options.map(String)
        : this.options.map(() => null);

      if (this.sizeIndex >= 0 && !explicit) {
        this.selected[this.sizeIndex] = null;
      }

      this.path = explicit && this.sizeIndex >= 0
        ? (this.isKit(this.selected[this.sizeIndex]) ? 'kit' : 'known')
        : null;
    }

    values(index) {
      return (this.options[index]?.values || []).map(value =>
        String(
          typeof value === 'object'
            ? value.name ?? value.value ?? ''
            : value
        )
      );
    }

    isKit(value) {
      return value != null && this.kitValues.includes(String(value));
    }

    matchesVariant(variant, selection = this.selected, ignored = []) {
      return variant.options.every((value, index) =>
        ignored.includes(index) ||
        selection[index] == null ||
        String(value) === String(selection[index])
      );
    }

    exactVariant() {
      if (this.selected.some(value => value == null)) return null;

      return this.variants.find(variant =>
        this.matchesVariant(variant)
      ) || null;
    }

    displayVariant() {
      return (
        this.exactVariant() ||
        this.variants.find(variant =>
          variant.available &&
          this.matchesVariant(variant, this.selected, [this.sizeIndex])
        ) ||
        this.variants.find(variant =>
          this.matchesVariant(variant, this.selected, [this.sizeIndex])
        ) ||
        null
      );
    }

    text(name, fallback) {
      return this.strings[name] || fallback;
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach(element => {
        element.textContent = value == null ? '' : value;
      });
    }

    clearError() {
      this.querySelectorAll('[data-error]').forEach(element => {
        element.textContent = '';
        element.hidden = true;
      });
    }

    showError(message) {
      this.querySelectorAll('[data-error]').forEach(element => {
        element.textContent = message;
        element.hidden = false;
      });
    }

    onClick(event) {
      const target = event.target.closest('button,a');
      if (!target || !this.contains(target)) return;

      if (target.matches('[data-option-index][data-option-value]')) {
        event.preventDefault();

        if (!target.disabled && !this.busy) {
          this.selectOption(
            Number(target.dataset.optionIndex),
            target.dataset.optionValue
          );
        }
      } else if (target.matches('[data-size-path]')) {
        event.preventDefault();

        if (!target.disabled && !this.busy) {
          this.selectPath(target.dataset.sizePath);
        }
      } else if (target.matches('[data-atc]')) {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (this.busy || target.disabled) return;

        if (!this.exactVariant()) {
          this.promptSize();
        } else {
          this.onSubmit(event);
        }
      } else if (target.matches('[data-thumb-step]')) {
        event.preventDefault();
        if (!target.disabled) {
          const next = Math.max(1, Math.min(this.gallerySlides().length - 1,
            this.galleryIndex + Number(target.dataset.thumbStep)));
          this.goToSlide(next);
        }
      } else if (target.matches('[data-gallery-dot]')) {
        event.preventDefault();
        this.goToSlide(Number(target.dataset.galleryDot));
      } else if (target.matches('[data-open-dialog]')) {
        event.preventDefault();

        const dialog = document.getElementById(target.dataset.openDialog);

        if (dialog && this.contains(dialog)) {
          this.dialogOpener = target;

          if (typeof dialog.showModal === 'function') {
            dialog.showModal();
          } else {
            dialog.setAttribute('open', '');
          }
        }
      } else if (target.matches('[data-close-dialog]')) {
        event.preventDefault();

        const dialog = target.closest('dialog');

        if (typeof dialog?.close === 'function') {
          dialog.close();
        } else {
          dialog?.removeAttribute('open');
        }

        this.dialogOpener?.focus({ preventScroll: true });
      }
    }

    selectOption(index, value) {
      if (
        !Number.isInteger(index) ||
        index < 0 ||
        index >= this.options.length ||
        !this.values(index).includes(String(value))
      ) return;

      this.clearError();
      this.selected[index] = String(value);

      if (index === this.modelIndex && this.colorIndex >= 0) {
        const compatible = this.variants.some(variant =>
          this.matchesVariant(variant, this.selected, [this.sizeIndex])
        );

        if (!compatible) {
          const next =
            this.variants.find(variant =>
              variant.available &&
              String(variant.options[index]) === value
            ) ||
            this.variants.find(variant =>
              String(variant.options[index]) === value
            );

          if (next) {
            this.selected[this.colorIndex] =
              String(next.options[this.colorIndex]);
          }
        }
      }

      if (this.sizeIndex >= 0 && index !== this.sizeIndex) {
        if (this.path === 'kit') {
          this.chooseKitValue();
        } else if (
          this.selected[this.sizeIndex] != null &&
          !this.exactVariant()?.available
        ) {
          this.selected[this.sizeIndex] = null;
        }
      }

      if (index === this.sizeIndex) {
        this.path = this.isKit(value) ? 'kit' : 'known';
      }

      const visualSelection =
        index === this.modelIndex || index === this.colorIndex;

      if (visualSelection) this.variantGalleryActive = true;

      this.render(true);

      if (visualSelection) {
        const imageUpdated = this.updateGalleryImage(this.displayVariant(), true);
        if (index === this.colorIndex && imageUpdated) {
          this.scrollToVariantGallery();
        }
      }
    }

    chooseKitValue() {
      const matching = this.variants.filter(variant =>
        this.matchesVariant(variant, this.selected, [this.sizeIndex]) &&
        this.isKit(variant.options[this.sizeIndex])
      );

      const kit = matching.find(variant => variant.available) || matching[0];

      this.selected[this.sizeIndex] = kit
        ? String(kit.options[this.sizeIndex])
        : null;
    }

    selectPath(path) {
      if (this.sizeIndex < 0 || !['known', 'kit'].includes(path)) return;
      if (path === this.path) return;

      this.clearError();
      this.path = path;
      this.selected[this.sizeIndex] = null;

      if (path === 'kit') this.chooseKitValue();

      this.render(true);
    }

    promptSize() {
      this.showError(
        this.text(
          'chooseSize',
          'Choose your ring size or the free sizing kit.'
        )
      );

      const area =
        this.querySelector('[data-size-section]') ||
        this.querySelector('[data-size-pane="known"]') ||
        this.form;

      area?.scrollIntoView({
        behavior: this.reducedMotion() ? 'auto' : 'smooth',
        block: 'center'
      });

      const first = area?.querySelector(
        'button:not([disabled]):not([hidden])'
      );

      first?.focus({ preventScroll: true });
    }

    render(updateUrl = false) {
      const current = this.exactVariant();
      const display = this.displayVariant();
      const ready = Boolean(current?.available);

      this.querySelectorAll(
        '[data-option-index][data-option-value]'
      ).forEach(button => {
        const index = Number(button.dataset.optionIndex);
        const value = button.dataset.optionValue;
        const hypothetical = this.selected.slice();

        hypothetical[index] = value;

        const ignored = index === this.modelIndex
          ? this.options
              .map((_, position) => position)
              .filter(position => position !== index)
          : index === this.sizeIndex
            ? []
            : [this.sizeIndex];

        const choices = this.variants.filter(variant =>
          this.matchesVariant(variant, hypothetical, ignored)
        );

        const selected = this.selected[index] === value;

        button.hidden =
          choices.length === 0 ||
          (index === this.sizeIndex && this.isKit(value));

        button.disabled =
          this.busy || !choices.some(variant => variant.available);

        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));

        if (index === this.colorIndex) {
          button.setAttribute('aria-label', englishColour(value));
          button.setAttribute('title', englishColour(value));
          const colourLabel = button.querySelector('[data-color-label]') || button.querySelector('span');
          if (colourLabel) colourLabel.textContent = englishColour(value);
          let image = button.querySelector('[data-color-image]');

          const variantImage = choices.find(
            variant => variant.image?.src
          )?.image;

          const label =
            button.querySelector('[data-color-label]') ||
            button.querySelector('span');

          if (variantImage) {
            if (!image) {
              image = document.createElement('img');
              image.setAttribute('data-color-image', '');
              image.width = 160;
              image.height = 160;
              button.prepend(image);
            }

            image.hidden = false;
            image.src = variantImage.src;
            image.removeAttribute('srcset');
            image.alt =
              `${this.selected[this.modelIndex] || ''} ${englishColour(value)}`.trim();
          } else if (image) {
            image.hidden = true;
            image.removeAttribute('src');
            image.removeAttribute('srcset');
          }

          label?.classList.toggle(
            'fpdf__sr-only',
            Boolean(variantImage)
          );
        }
      });

      this.querySelectorAll('[data-size-path]').forEach(button => {
        const kit = button.dataset.sizePath === 'kit';

        const kitAvailable = this.variants.some(variant =>
          variant.available &&
          this.matchesVariant(variant, this.selected, [this.sizeIndex]) &&
          this.isKit(variant.options[this.sizeIndex])
        );

        button.hidden =
          this.sizeIndex < 0 ||
          (kit && this.kitValues.length === 0);

        button.disabled = this.busy || (kit && !kitAvailable);

        button.classList.toggle(
          'is-selected',
          button.dataset.sizePath === this.path
        );

        button.setAttribute(
          'aria-pressed',
          String(button.dataset.sizePath === this.path)
        );
      });

      this.querySelectorAll('[data-size-pane]').forEach(pane => {
        pane.hidden =
          this.sizeIndex < 0 ||
          pane.dataset.sizePane !== this.path;
      });

      if (this.idInput) {
        this.idInput.value = ready ? String(current.id) : '';
        this.idInput.disabled = !ready;
      }

      const price =
        display?.price_formatted ?? this.formatMoney(display?.price);

      this.setText('[data-price]', price);
      this.setText('[data-kit-price]', price);

      document.querySelectorAll(
        '[data-fpdf-comparison-price]'
      ).forEach(element => {
        element.textContent = price;
      });

      const hasDiscount = Boolean(
        display &&
        Number(display.compare_at_price) > Number(display.price)
      );

      this.setText(
        '[data-compare]',
        hasDiscount
          ? display.compare_formatted ??
            this.formatMoney(display.compare_at_price)
          : ''
      );

      this.querySelectorAll('[data-compare],[data-sale]').forEach(element => {
        element.hidden = !hasDiscount;
      });

      if (this.colorIndex >= 0) {
        this.setText('[data-color-name]', englishColour(this.selected[this.colorIndex]));
      }

      let label = this.path === 'kit'
        ? this.text('kitButton', 'Buy your ring with the free sizing kit')
        : this.text('addToCart', 'Buy your ring');

      if (!current) {
        label = this.sizeIndex >= 0 && this.selected[this.sizeIndex] == null
          ? this.text('chooseSize', 'Choose your size')
          : this.text('unavailable', 'Unavailable');
      } else if (!current.available) {
        label = this.text('soldOut', 'Sold out');
      }

      this.querySelectorAll('[data-atc]').forEach(button => {
        const labelElement =
          button.querySelector('[data-atc-label]') || button;

        labelElement.textContent =
          ready &&
          this.path === 'known' &&
          button.hasAttribute('data-main-atc')
            ? `${label} · ${price}`
            : label;

        button.disabled =
          this.busy ||
          Boolean(current && !current.available) ||
          (
            !current &&
            (
              this.sizeIndex < 0 ||
              this.selected[this.sizeIndex] != null
            )
          );

        button.setAttribute('aria-busy', String(this.busy));
      });

      this.querySelectorAll('[data-payment]').forEach(element => {
        element.hidden = !ready || this.busy;
      });

      this.renderShipping(display);
      this.updateGalleryImage(display);

      const amount = ((display?.price || 0) / 100).toFixed(2);

      this.querySelectorAll('[data-pp-message]').forEach(message => {
        if (message.getAttribute('data-pp-amount') !== amount) {
          message.setAttribute('data-pp-amount', amount);
        }
      });

      if (updateUrl) {
        const url = new URL(location.href);

        if (current) {
          url.searchParams.set('variant', current.id);
        } else {
          url.searchParams.delete('variant');
        }

        history.replaceState(history.state, '', url.toString());
      }

      if (String(current?.id || '') !== String(this.lastVariantId || '')) {
        const previousVariant = this.variants.find(variant =>
          String(variant.id) === String(this.lastVariantId)
        );

        this.lastVariantId = current?.id || null;

        this.idInput?.dispatchEvent(
          new Event('change', { bubbles: true })
        );

        this.form?.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: {
              formId: this.form.getAttribute('id'),
              variant: current,
              previousVariant
            }
          })
        );
      }

      this.updateSticky();
    }

    formatMoney(cents) {
      if (cents == null) return '';

      try {
        return new Intl.NumberFormat(
          document.documentElement.lang || 'en-US',
          {
            style: 'currency',
            currency: this.data.currency || 'USD'
          }
        ).format(Number(cents) / 100);
      } catch (_) {
        return (Number(cents) / 100).toFixed(2);
      }
    }

    localizeStock(value) {
      return String(value || '').replace(/Lieferzeit/gi, 'Delivery time').replace(/Werktage/gi, 'working days').replace(/Tagen/gi, 'days').replace(/Tage/gi, 'days').replace(/Auf Lager/gi, 'In stock').replace(/Sofort lieferbar/gi, 'Available immediately').replace(/Ausverkauft/gi, 'Sold out').replace(/Versand ab/gi, 'Dispatch from');
    }

    renderShipping(variant) {
      const unavailable = variant && !variant.available;

      const stock = unavailable
        ? this.text('soldOut', 'Sold out')
        : this.settings.deliveryText || 'Dispatch depends on the availability of your selected variant.';

      this.setText('[data-stock-text]', stock);

      this.querySelectorAll('[data-stock-dot]').forEach(dot => {
        const color = unavailable
          ? '#888888'
          : variant?.stock_color || '#6fcb97';

        if (CSS.supports('color', color)) {
          dot.style.backgroundColor = color;
        }
      });

      const fast =
        !unavailable && /1\s*[-–—]\s*3\s*(?:tage|days)/i.test(stock);

      const estimate = fast
        ? 'Order before 11 am: dispatched today.'
        : '';

      this.setText('[data-shipping-estimate]', estimate);

      this.querySelectorAll('[data-shipping-estimate]').forEach(element => {
        element.hidden = !estimate;
      });

      this.setText(
        '[data-shipment-type]',
        this.path === 'kit'
          ? this.text('kitShipment', 'Your sizing kit arrives first.')
          : this.text('ringShipment', 'Your ring is dispatched directly.')
      );
    }

    shippingEstimate() {
      let parts;

      try {
        parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.settings.timezone || 'Europe/Berlin',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          hourCycle: 'h23'
        }).formatToParts(new Date());
      } catch (_) {
        return '';
      }

      const info = Object.fromEntries(
        parts
          .filter(part => part.type !== 'literal')
          .map(part => [part.type, Number(part.value)])
      );

      const today = new Date(
        Date.UTC(info.year, info.month - 1, info.day)
      );

      const cutoff = Number(this.settings.cutoff ?? 11);

      if (info.hour < cutoff && this.isBusinessDay(today)) {
        return this.text('today', 'Dispatched today');
      }

      return '';
    }

    isBusinessDay(date) {
      if ([0, 6].includes(date.getUTCDay())) return false;

      const year = date.getUTCFullYear();
      const iso = date.toISOString().slice(0, 10);

      const holidays = new Set(
        String(this.settings.holidays || '')
          .split(/[\n,;]+/)
          .map(value => value.trim())
      );

      ['01-01', '05-01', '10-03', '12-25', '12-26'].forEach(day => {
        holidays.add(`${year}-${day}`);
      });

      const a = year % 19;
      const b = Math.floor(year / 100);
      const c = year % 100;
      const d = Math.floor(b / 4);
      const e = b % 4;
      const f = Math.floor((b + 8) / 25);
      const g = Math.floor((b - f + 1) / 3);
      const h = (19 * a + b - d - g + 15) % 30;
      const i = Math.floor(c / 4);
      const k = c % 4;
      const l = (32 + 2 * e + 2 * i - h - k) % 7;
      const m = Math.floor((a + 11 * h + 22 * l) / 451);

      const easter = new Date(
        Date.UTC(
          year,
          Math.floor((h + l - 7 * m + 114) / 31) - 1,
          (h + l - 7 * m + 114) % 31 + 1
        )
      );

      [-2, 1, 39, 50].forEach(offset => {
        const holiday = new Date(easter);
        holiday.setUTCDate(holiday.getUTCDate() + offset);
        holidays.add(holiday.toISOString().slice(0, 10));
      });

      return !holidays.has(iso);
    }

    setupGallery() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;

      this.heroSlide = this.querySelector('.fpdf__slide--hero');

      this.heroContents = this.heroSlide
        ? Array.from(this.heroSlide.children)
        : [];

      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.galleryFrame);

        this.galleryFrame = requestAnimationFrame(() =>
          this.updateGalleryPosition()
        );
      }, { passive: true, signal: this.abort.signal });

      window.addEventListener('resize', () => {
        this.goToSlide(this.galleryIndex || 0);
      }, { signal: this.abort.signal });

      this.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('[data-gallery-item]');
        const index = this.gallerySlides().indexOf(slide);
        if (index >= 0) this.goToSlide(index);
      }, { signal: this.abort.signal });

      this.setGalleryIndex(0, this.gallerySlides().length);
    }

    gallerySlides() {
      return this.track
        ? Array.from(this.track.querySelectorAll('[data-gallery-item]'))
        : [];
    }

    goToSlide(index) {
      const slides = this.gallerySlides();
      const next = Math.max(0, Math.min(index, slides.length - 1));
      const slide = slides[next];

      if (!slide) return;

      const desktop = getComputedStyle(this.track).display === 'grid';
      const preview = desktop && next > 0;

      if (preview && this.heroSlide && !this.variantPreview) {
        this.variantPreview = document.createElement('img');
        this.variantPreview.className = 'fpdf__hero-image';
        this.variantPreview.style.objectFit = 'contain';
        this.heroSlide.append(this.variantPreview);
      }

      this.heroContents?.forEach(element => {
        element.hidden = preview;

        if (preview) {
          if (!element.hasAttribute('data-original-display')) {
            element.dataset.originalDisplay = element.style.display;
          }

          element.style.display = 'none';

          if (element.matches('video')) element.pause();
        } else if (element.hasAttribute('data-original-display')) {
          element.style.display = element.dataset.originalDisplay;

          if (element.matches('video[autoplay]')) {
            element.play()?.catch(() => {});
          }
        }
      });

      if (this.variantPreview) {
        this.variantPreview.hidden = !preview;
        this.variantPreview.style.display = preview ? '' : 'none';

        const source = slide.querySelector('img');

        if (preview && source) {
          this.variantPreview.src = source.currentSrc || source.src;
          this.variantPreview.alt = source.alt;
        }
      }

      if (!desktop) {
        this.track.scrollTo({
          left:
            this.track.scrollLeft +
            slide.getBoundingClientRect().left -
            this.track.getBoundingClientRect().left,
          behavior: 'instant'
        });
      }

      this.setGalleryIndex(next, slides.length);
    }

    updateGalleryPosition() {
      if (!this.track || getComputedStyle(this.track).display === 'grid') {
        return;
      }

      const slides = this.gallerySlides();
      const left = this.track.getBoundingClientRect().left;

      let best = 0;
      let distance = Infinity;

      slides.forEach((slide, index) => {
        const gap = Math.abs(slide.getBoundingClientRect().left - left);

        if (gap < distance) {
          best = index;
          distance = gap;
        }
      });

      this.setGalleryIndex(best, slides.length);
    }

    setGalleryIndex(index, count) {
      const changed = this.galleryIndex !== index;
      this.galleryIndex = index;
      this.querySelectorAll('[data-gallery-dot]').forEach(dot => {
        const active = Number(dot.dataset.galleryDot) === index;
        dot.classList.toggle('is-selected', active);
        dot.setAttribute('aria-current', String(active));
        if (active && changed && dot.parentElement) {
          const rail = dot.parentElement;
          const left = rail.scrollLeft + dot.getBoundingClientRect().left
            - rail.getBoundingClientRect().left + dot.offsetWidth / 2 - rail.clientWidth / 2;
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          rail.scrollTo({ left: Math.max(0, Math.min(left, rail.scrollWidth - rail.clientWidth)),
            behavior: reducedMotion ? 'instant' : 'smooth' });
        }
      });
      const previous = this.querySelector('[data-thumb-step="-1"]');
      const next = this.querySelector('[data-thumb-step="1"]');
      if (previous) previous.disabled = index <= 1;
      if (next) next.disabled = index >= count - 1;
      this.setText('[data-gallery-count]', `${index + 1} / ${count}`);
    }

    updateGalleryImage(variant, force = false) {
      if (!this.variantGalleryActive) return false;

      const matching = candidate =>
        this.matchesVariant(candidate, this.selected, [this.sizeIndex]);

      const selected =
        variant?.image?.src && matching(variant)
          ? variant
          : this.variants.find(candidate =>
              candidate.image?.src && matching(candidate)
            );

      if (!selected) return false;

      if (!this.heroSlide) return false;

      if (force || this.lastGallerySource !== selected.image.src) {
        const image = document.createElement('img');
        image.className = 'fpdf__hero-image';
        image.src = selected.image.src;
        image.alt = selected.options.filter((_, position) => position !== this.sizeIndex).join(' – ');
        image.width = 1400;
        image.height = 1400;
        this.heroSlide.querySelectorAll('video').forEach(video => video.pause());
        this.heroSlide.replaceChildren(image);
        this.heroContents = [image];
        this.variantPreview = null;
        this.heroSlide.dataset.mediaId = String(selected.image.id || selected.image.src);
        this.goToSlide(0);
      }

      this.lastGallerySource = selected.image.src;

      this.querySelectorAll(
        '[data-zoom-image],[data-sticky-image]'
      ).forEach(image => {
        image.src = selected.image.src;
        image.removeAttribute('srcset');
        image.alt = selected.options
          .filter((_, position) => position !== this.sizeIndex)
          .join(' – ');
      });

      return true;
    }

    scrollToVariantGallery() {
      cancelAnimationFrame(this.variantScrollFrame);

      if (!window.matchMedia('(max-width: 749px)').matches) return;

      const gallery = this.querySelector('.fpdf__gallery');
      if (!gallery) return;

      this.variantScrollFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;

        const header =
          parseFloat(
            getComputedStyle(this).getPropertyValue('--header-height')
          ) || 0;

        window.scrollTo({
          top: Math.max(
            0,
            window.scrollY +
              gallery.getBoundingClientRect().top -
              header -
              16
          ),
          behavior: this.reducedMotion() ? 'auto' : 'smooth'
        });
      });
    }

    reducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }

    setupSticky() {
      this.sticky = this.querySelector('[data-sticky]');
      this.mainAtc = this.querySelector('[data-main-atc]');

      this.footer =
        document.querySelector('#fpdf-page-footer') ||
        document.querySelector(
          '.shopify-section-group-fitorb-pdf-footer-group,' +
          '.shopify-section-group-footer-group'
        );

      if (!this.sticky || !this.mainAtc) return;

      const update = () => {
        if (this.scrollFrame) return;

        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateSticky();
        });
      };

      window.addEventListener('scroll', update, {
        passive: true,
        signal: this.abort.signal
      });

      window.addEventListener('resize', update, {
        passive: true,
        signal: this.abort.signal
      });

      if ('IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver(update);
        this.stickyObserver.observe(this.mainAtc);

        if (this.footer) {
          this.stickyObserver.observe(this.footer);
        }
      }
    }

    updateSticky() {
      if (!this.sticky || !this.mainAtc) return;

      const belowButton = this.mainAtc.getBoundingClientRect().bottom < 0;

      const footerVisible =
        this.footer &&
        this.footer.getBoundingClientRect().top < innerHeight;

      const shown = belowButton && !footerVisible;

      this.sticky.hidden = !shown;
      this.sticky.classList.toggle('is-visible', shown);
      this.classList.toggle('has-sticky', shown);
    }

    async onSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.busy) return;

      const variant = this.exactVariant();

      if (!variant) {
        this.promptSize();
        return;
      }

      if (!variant.available) {
        this.showError(
          this.text('soldOut', 'This variant is sold out.')
        );
        return;
      }

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      this.clearError();
      this.busy = true;
      this.render();

      const root = window.Shopify?.routes?.root || '/';
      let added = false;

      try {
        const body = new FormData(this.form);
        body.set('id', String(variant.id));
        body.set('quantity', body.get('quantity') || '1');
        body.delete('sections');
        body.delete('sections_url');
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', body,
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
        });
        added = response.ok;
        if (!added) {
          const result = await response.json();
          throw new Error(typeof result.description === 'string'
            ? result.description
            : this.text('error', 'The ring could not be added. Please try again.'));
        }
        // Keep the submission locked while the checkout navigation starts.
        location.assign(`${root}checkout`);
      } catch (error) {
        if (added) {
          location.assign(`${root}checkout`);
        } else {
          const message =
            error instanceof TypeError || error instanceof SyntaxError
              ? this.text(
                  'error',
                  'Connection lost. Please check your cart before trying again.'
                )
              : error.message || this.text(
                  'error',
                  'We could not confirm that the product was added. Please check your cart.'
                );

          this.showError(message);

          this.form.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: { error: message }
            })
          );
        }
      } finally {
        if (!added) {
          this.busy = false;
          if (this.isConnected) this.render();
        }
      }
    }
  }

  customElements.define('fitorb-us-product', FitorbPdfProduct);
})();

